/**
 * Pie de un listado con scroll infinito.
 *
 * Tiene el marcador que, al acercarse a la pantalla, pide la pagina siguiente,
 * y dice en que va la carga: trayendo mas, fallo (con reintentar, porque un
 * corte de senal en el celular no puede dejar la lista trabada para siempre) o
 * ya se mostro todo.
 *
 * El "Mostrando X de Y" va en un `role="status"`: quien usa lector de pantalla
 * no ve aparecer tarjetas nuevas y necesita que se lo digan.
 *
 * Con `enlaceSiguiente` (categorias y lineas) hay ademas un enlace de verdad a
 * la pagina siguiente: Google no baja la pagina, pero si sigue enlaces, y asi
 * llega a todos los productos. Al comprador el mismo enlace le suma la pagina
 * aca mismo, como el scroll.
 */
import { Link } from 'react-router-dom';

import { useAlVer } from '../../hooks/use-al-ver';
import type { ErrorApi } from '../../tipos/api';
import { numero } from '../../utilidades/formato';

export interface CargaSiguienteProps {
  cargados: number;
  total: number;
  quedanMas: boolean;
  cargando: boolean;
  error: ErrorApi | null;
  cargarMas: () => void;
  /** URL de la pagina siguiente, para los listados que se indexan. */
  enlaceSiguiente?: string | undefined;
}

export function CargaSiguiente({
  cargados,
  total,
  quedanMas,
  cargando,
  error,
  cargarMas,
  enlaceSiguiente,
}: CargaSiguienteProps) {
  // Con un error a la vista no se reintenta solo: lo decide el comprador.
  const observar = quedanMas && error === null;
  const marcador = useAlVer(cargarMas, [cargados, observar]);

  return (
    <div className="carga-siguiente">
      {observar ? <div ref={marcador} aria-hidden="true" /> : null}

      <p role="status" className={cargando ? 'cargando' : 'carga-siguiente-cuenta'}>
        {quedanMas
          ? `Mostrando ${numero(cargados)} de ${numero(total)} productos`
          : 'Llegaste al final de los resultados'}
      </p>

      {observar && !cargando && enlaceSiguiente !== undefined ? (
        <Link
          className="btn"
          to={enlaceSiguiente}
          rel="next"
          onClick={(evento) => {
            evento.preventDefault();
            cargarMas();
          }}
        >
          Ver más productos
        </Link>
      ) : null}

      {error !== null ? (
        <div className="carga-siguiente-error" role="alert">
          <p>{error.mensaje}</p>
          <button type="button" className="btn" onClick={cargarMas}>
            Reintentar
          </button>
        </div>
      ) : null}
    </div>
  );
}
